import { useState } from "react";
import { Plus, Users, ChevronDown, ChevronUp } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { SimpleList } from "../simple-list/SimpleList";
import { Avatar } from "../contacts/Avatar";
import type { Contact } from "../types";
import { ScrollableCard } from "./ScrollableCard";

export const HotContacts = ({
  collapsible = false,
}: {
  collapsible?: boolean;
}) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(true);

  const showChevron = collapsible && isMobile;
  const {
    data: contactData,
    total: contactTotal,
    isPending: contactsLoading,
  } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "last_seen", order: "DESC" },
      filter: { status: "hot", sales_id: identity?.id },
    },
    { enabled: Number.isInteger(identity?.id) },
  );

  return (
    <div className="flex flex-col md:h-full">
      <div className="flex items-center gap-2 pb-3 flex-none">
        <Users className="text-nd-text-disabled w-4 h-4" strokeWidth={1.5} />
        <span className="font-nd-mono text-[11px] tracking-[0.08em] uppercase text-nd-text-secondary flex-1">
          {translate("resources.contacts.hot.title")}
        </span>
        {!isMobile && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-nd-text-disabled hover:text-nd-text-secondary"
                  asChild
                >
                  <Link to="/contacts/create">
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {translate("resources.contacts.action.create")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {showChevron && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 text-nd-text-disabled hover:text-nd-text-secondary"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
            )}
          </Button>
        )}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateRows: !collapsible || expanded ? "1fr" : "0fr",
          transition: collapsible
            ? "grid-template-rows 0.3s ease-out"
            : undefined,
          overflow: "hidden",
        }}
      >
        <div style={{ minHeight: 0, overflow: "hidden" }}>
          <ScrollableCard className="py-0">
            <SimpleList<Contact>
              linkType="show"
              data={contactData}
              total={contactTotal}
              isPending={contactsLoading}
              resource="contacts"
              className="[&>li:first-child>a]:rounded-t-xl [&>li:last-child>a]:rounded-b-xl"
              primaryText={(contact) =>
                [contact.first_name, contact.last_name]
                  .filter(Boolean)
                  .join(" ") || "—"
              }
              secondaryText={(contact) => {
                const text =
                  contact.title && contact.company_name
                    ? translate("resources.contacts.position_at_company", {
                        title: contact.title,
                        company: contact.company_name,
                      })
                    : contact.title || contact.company_name;
                return text ? (
                  <span className="truncate block max-w-full" title={text}>
                    {text}
                  </span>
                ) : null;
              }}
              leftAvatar={(contact) => <Avatar record={contact} />}
              empty={
                <div className="p-4 flex flex-col gap-3">
                  <p className="text-sm text-nd-text-secondary">
                    {translate("resources.contacts.hot.empty_hint")}
                  </p>
                  <p className="text-sm text-nd-text-secondary">
                    {translate("resources.contacts.hot.empty_change_status")}
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="mt-1 w-full font-nd-mono text-[10px] tracking-[0.08em] uppercase"
                  >
                    <Link to="/contacts">
                      {translate("resources.contacts.name", {
                        smart_count: 2,
                      })}{" "}
                      →
                    </Link>
                  </Button>
                </div>
              }
            />
          </ScrollableCard>
        </div>
      </div>
    </div>
  );
};
